import {
  ICredential,
  TSupportedCredential,
} from '@commenda/types/src/credential/types';
import { Country, CredentialStatus } from '@prisma/client';

enum CredentialType {
  CORPORATE_TAX = 'CORPORATE_TAX',
  INDIRECT_TAX = 'INDIRECT_TAX',
  PAYROLL_TAX = 'PAYROLL_TAX',
  COMPANY_SECRETARIAL = 'COMPANY_SECRETARIAL',
  SOCIAL_SECURITY = 'SOCIAL_SECURITY',
  BANKING = 'BANKING',
  OTHER = 'OTHER',
}

export class CredentialHelper {
  static STATE_SALES_TAX_PORTALS = 'STATE_SALES_TAX_PORTALS';

  static CREDENTIAL_TYPE_MAP: Record<CredentialType, string> = {
    [CredentialType.CORPORATE_TAX]: 'Corporate Tax',
    [CredentialType.INDIRECT_TAX]: 'Indirect Tax',
    [CredentialType.PAYROLL_TAX]: 'Payroll Tax',
    [CredentialType.COMPANY_SECRETARIAL]: 'Company Secretarial',
    [CredentialType.SOCIAL_SECURITY]: 'Social Security',
    [CredentialType.BANKING]: 'Banking',
    [CredentialType.OTHER]: 'Other',
  };

  static CREDENTIAL_STATUS_MAP: Record<CredentialStatus, string> = {
    [CredentialStatus.ACTIVE]: 'Active',
    [CredentialStatus.EXPIRED]: 'Expired',
  };

  static getCredentialName(credential: ICredential) {
    if (!credential) return null;

    return credential.name || '-';
  }

  static getCredentialType(type: CredentialType) {
    return this.CREDENTIAL_TYPE_MAP[type] || '-';
  }

  static getCredentialStatus(status: CredentialStatus) {
    return this.CREDENTIAL_STATUS_MAP[status] || '-';
  }

  static getCredentialStatusBadgeVariant(status: CredentialStatus) {
    return status === CredentialStatus.ACTIVE ? 'success' : 'error';
  }

  static getUniqueIdentifiersForCredentials(
    supportedCredentials: TSupportedCredential[]
  ) {
    return Array.from(
      new Set(supportedCredentials.map((cred) => cred.internalIdentifier))
    );
  }

  static getCredentialOptions(supportedCredentials: TSupportedCredential[]) {
    const uniqueIdentifiers =
      this.getUniqueIdentifiersForCredentials(supportedCredentials);

    return uniqueIdentifiers
      .filter((identifier) => identifier !== this.STATE_SALES_TAX_PORTALS)
      .map((identifier) => ({
        value: identifier,
        displayedValue: supportedCredentials.find(
          (cred) => cred.internalIdentifier === identifier
        )?.name,
      }));
  }

  static getScopedJurisdictions(
    supportedCredentials: TSupportedCredential[],
    internalIdentifier: string,
    country: Country
  ): string[] {
    if (!internalIdentifier) return [];

    return Array.from(
      new Set(
        supportedCredentials
          .filter(
            (credential) =>
              credential.internalIdentifier === internalIdentifier &&
              credential.country === country &&
              credential.jurisdiction
          )
          .map((credential) => credential.jurisdiction)
      )
    );
  }

  static getCredentialTypeOptions() {
    return Object.entries(this.CREDENTIAL_TYPE_MAP).map(([key, value]) => ({
      value: key,
      label: value,
    }));
  }
}
